import { listProductConfigs } from '@/app/config/products.config';
import type { ProductEmissionMode } from '@/services/product-settings';
import { useProductSettings } from '@/shared/hooks';
import { ConfirmActionModal } from '@/shared/ui/confirm-action-modal.component';
import { toaster } from '@/shared/ui/toaster.component';
import { Button, Tag } from '@noorden-insurtech/alba-design-system';
import { useMemo, useState } from 'react';
import { useUpdateProductEmissionMode } from '../hooks/use-update-product-emission-mode.hook';

interface PendingChange {
  productCode: string;
  productName: string;
  emissionMode: ProductEmissionMode;
}

const MODE_LABEL: Record<ProductEmissionMode, string> = {
  INTEGRATION: 'Integração',
  MANUAL: 'Manual',
};

interface ProductEmissionSettingsProps {
  /** Só o Administrador altera; sem permissão a tela fica somente leitura. */
  canEdit: boolean;
}

/**
 * Configuração do modo de emissão por produto — autoatendimento da área, sem deploy.
 *
 * A lista de produtos vem do registry do front (`products.config.ts`) e o modo, do
 * BFF: produto sem configuração gravada aparece como "Integração", que é o padrão
 * do domínio. Produto novo passa a aparecer aqui só por entrar no registry.
 */
export function ProductEmissionSettings({
  canEdit,
}: ProductEmissionSettingsProps) {
  const { data, isLoading, error } = useProductSettings();
  const updateMutation = useUpdateProductEmissionMode();

  const [pending, setPending] = useState<PendingChange | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const modeByProduct = useMemo(() => {
    const map = new Map<string, ProductEmissionMode>();
    (data ?? []).forEach(settings =>
      map.set(settings.productCode, settings.emissionMode)
    );
    return map;
  }, [data]);

  const products = useMemo(
    () =>
      listProductConfigs().map(config => ({
        productCode: config.productCode,
        productName: config.displayName,
        emissionMode: modeByProduct.get(config.productCode) ?? 'INTEGRATION',
      })),
    [modeByProduct]
  );

  const closePending = () => {
    if (updateMutation.isPending) return;
    setPending(null);
    setActionError(null);
  };

  /** Erro de negócio fica no modal, sem fechá-lo. */
  const confirmPending = async () => {
    if (!pending) return;
    setActionError(null);

    const response = await updateMutation.mutateAsync({
      productCode: pending.productCode,
      emissionMode: pending.emissionMode,
    });

    if (!response.success) {
      setActionError(
        response.error || 'Não foi possível alterar o modo de emissão.'
      );
      return;
    }

    toaster.create({
      type: 'success',
      title: 'Modo de emissão alterado',
      description: `${pending.productName} passou para "${MODE_LABEL[pending.emissionMode]}".`,
    });
    setPending(null);
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12 text-alba-gray-500'>
        Carregando...
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center py-12 text-alba-error-red-600'>
        {error instanceof Error
          ? error.message
          : 'Erro ao carregar as configurações de produto'}
      </div>
    );
  }

  return (
    <>
      <ConfirmActionModal
        isOpen={Boolean(pending)}
        onClose={closePending}
        onConfirm={confirmPending}
        isLoading={updateMutation.isPending}
        errorMessage={actionError}
        title='Alterar o modo de emissão'
        confirmLabel='Alterar'
        description={
          pending ? (
            <>
              O produto{' '}
              <span className='font-semibold text-alba-black-500'>
                {pending.productName}
              </span>{' '}
              passará para o modo{' '}
              <span className='font-semibold text-alba-black-500'>
                {MODE_LABEL[pending.emissionMode]}
              </span>
              .
            </>
          ) : null
        }
        warning={
          pending?.emissionMode === 'MANUAL' ? (
            <>
              Em modo <strong>Manual</strong>, as propostas transmitidas deste
              produto <strong>deixam de ser emitidas automaticamente</strong> no
              i4Pro: a área é avisada por e-mail e um administrador precisa
              informar o número de cada apólice no Portal.
            </>
          ) : (
            <>
              Em modo <strong>Integração</strong>, as próximas propostas
              transmitidas voltam a ser emitidas automaticamente no i4Pro. As
              que já estão aguardando emissão manual{' '}
              <strong>continuam aguardando</strong>: em Integrações, cada uma
              pode receber o número da apólice (Emitir manualmente) ou passar
              por uma tentativa de emissão automática.
            </>
          )
        }
      />

      <div className='space-y-4'>
        <div>
          <h2 className='text-lg font-semibold text-alba-black-500'>
            Modo de emissão
          </h2>
          <p className='mt-1 text-sm text-alba-gray-600'>
            Em <strong>Integração</strong> (padrão), a apólice é emitida
            automaticamente no i4Pro ao transmitir a proposta. Em{' '}
            <strong>Manual</strong>, a emissão é feita fora do Portal e um
            administrador informa o número da apólice.
          </p>
        </div>

        <div className='overflow-hidden rounded-xl border border-alba-gray-200 bg-white'>
          <ul className='divide-y divide-alba-gray-200'>
            {products.map(product => {
              const isManual = product.emissionMode === 'MANUAL';
              const nextMode: ProductEmissionMode = isManual
                ? 'INTEGRATION'
                : 'MANUAL';

              return (
                <li
                  key={product.productCode}
                  className='flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between'
                >
                  <div className='min-w-0'>
                    <p className='truncate font-medium text-alba-black-500'>
                      {product.productName}
                    </p>
                    <p className='text-sm text-alba-gray-500'>
                      {product.productCode}
                    </p>
                  </div>

                  <div className='flex items-center gap-3'>
                    <Tag.Root
                      variant='outline'
                      colorScheme={isManual ? 'warning' : 'success'}
                      size='sm'
                    >
                      <Tag.Label>{MODE_LABEL[product.emissionMode]}</Tag.Label>
                    </Tag.Root>

                    {canEdit && (
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() =>
                          setPending({
                            productCode: product.productCode,
                            productName: product.productName,
                            emissionMode: nextMode,
                          })
                        }
                      >
                        Mudar para {MODE_LABEL[nextMode]}
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
}
